import { createContext, useContext } from "react";

export type AppTokens =
  | {
      // server shape (supports either field name)
      Token?: string;                 // legacy
      AccessToken?: string;           // future-proof
      TokenExpiresAtUnixMs?: number;  // from API (preferred)
      RefreshToken?: string | null;   // optional (future)
      RefreshTokenExpiresAtUnixMs?: number | null;
    }
  | null;

export type AuthContextType = {
  authTokens: AppTokens;
  setTokens: (data: AppTokens) => void;
  logout: () => void;
  isAuthenticated: boolean;
};

export const AuthContext = createContext<AuthContextType>({
  authTokens: null,
  setTokens: () => {},
  logout: () => {},
  isAuthenticated: false,
});

export const useAuth = () => useContext(AuthContext);

// ---- helpers
export function getAccessToken(t: AppTokens): string | null {
  if (!t) return null;
  return t.AccessToken ?? t.Token ?? null;
}

function base64UrlJsonDecode(str: string) {
  const pad = (s: string) => s + "===".slice((s.length + 3) % 4);
  const b64 = pad(str).replace(/-/g, "+").replace(/_/g, "/");
  try {
    return JSON.parse(atob(b64));
  } catch {
    return {};
  }
}

export function tokenExpired(tokens: AppTokens): boolean {
  if (!tokens) return true;

  // 1) Prefer explicit server expiry
  if (typeof tokens.TokenExpiresAtUnixMs === "number") {
    return Date.now() >= tokens.TokenExpiresAtUnixMs;
  }

  // 2) Fallback: decode JWT exp
  const raw = getAccessToken(tokens);
  if (!raw) return true;

  const parts = raw.split(".");
  if (parts.length !== 3) return true;
  const payload: any = base64UrlJsonDecode(parts[1]);
  if (typeof payload.exp === "number") {
    const nowSec = Math.floor(Date.now() / 1000);
    return payload.exp <= nowSec;
  }

  // If we can't tell, treat as expired
  return true;
}
